'use client';

import { TrackedAnchor } from '@/components/ancher';
import { LINKS } from '@/constants';
import { ReactNode } from 'react';
import { sanitizeText } from '@/utils';

const InternalLink = ({
  href,
  children,
}: {
  href: string;
  children: string;
}) => {
  const text = sanitizeText(children);
  return (
    <TrackedAnchor
      label={`bottom_description_${text}`}
      position="main_content"
      href={href}
      className="text-3b-text-secondary underline"
    >
      {children}
    </TrackedAnchor>
  );
};

const BulletPoint = ({
  number,
  children,
}: {
  number: number;
  children: ReactNode;
}) => (
  <div className="relative ml-3">
    <span className="absolute -left-3">{number}.</span> {children}
  </div>
);

function Description() {
  return (
    <div className="self-stretch text-[12px] font-normal leading-[18px] tracking-[0.016px] text-3b-text-secondary">
      <BulletPoint number={1}>
        The gene name links to{' '}
        <InternalLink href={LINKS.GENE_BROWSER}>
          3billion's gene browser
        </InternalLink>
        , which shows how many individuals have been diagnosed with each gene.
        If there is an 'E' and/or 'G' next to the gene name, it indicates that
        the gene contains coding exon(s) that overlaps with NGS dead zone when
        sequenced using{' '}
        <InternalLink href={LINKS.WHOLE_EXOME}>3B-EXOME</InternalLink> or{' '}
        <InternalLink href={LINKS.WHOLE_GENOME}>3B-GENOME</InternalLink>,
        respectively, and as a result the coverage shown in the right column(s)
        will have been compromised. NGS dead zones are regions with poor
        coverage due to high GC content or repetitive sequences, including
        segmental duplications and pseudogenes.
      </BulletPoint>

      <BulletPoint number={2}>
        The percentage of coding exon bases within each gene with at least 20
        reads of good mapping quality. Coding exons are defined by the MANE
        transcript and good mapping quality reads are defined as reads with a
        mapping quality of ≥20. Only coding exons (CDS regions) were included in
        the calculation, so the exon count may differ from the actual total exon
        number.
      </BulletPoint>

      <BulletPoint number={3}>
        The percentage of coding exon bases within each gene with at least 10
        reads of good mapping quality. Coding exons are defined by the MANE
        transcript and good mapping quality reads are defined as reads with a
        mapping quality of ≥20.
      </BulletPoint>

      <BulletPoint number={4}>
        The coverage of the gene is compromised because there are coding exons
        not captured in{' '}
        <InternalLink href={LINKS.WHOLE_EXOME}>3B-EXOME</InternalLink>.
      </BulletPoint>

      <BulletPoint number={5}>
        The data was last updated in Sep 2025.
      </BulletPoint>
    </div>
  );
}

export default Description;
